import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './NotificationsBell.css';

const API = process.env.REACT_APP_API_URL;

const Avatar = ({ username, photoUrl }) => {
  const initial = (username || '?')[0].toUpperCase();
  return photoUrl
    ? <img src={photoUrl} alt={username} className="nb-avatar-img" />
    : <div className="nb-avatar-placeholder">{initial}</div>;
};

const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (diff < 60)    return `${diff}s`;
  if (diff < 3600)  return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString('es-EC', { day: 'numeric', month: 'short' });
};

const NotificationsBell = ({ onFollowChanged }) => {
  const { t } = useTranslation();
  const [open, setOpen]            = useState(false);
  const [count, setCount]          = useState(0);
  const [notifications, setNotifs] = useState([]);
  const [loading, setLoading]      = useState(false);
  const panelRef = useRef(null);

  const token = () => localStorage.getItem('access_token');

  const fetchCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/notifications/count/`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setCount(r.data.count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications/`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setNotifs(r.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/notifications/read-all/`, {}, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setCount(0);
    } catch {}
  }, []);

  // Poll count every 30s
  useEffect(() => {
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => clearInterval(id);
  }, [fetchCount]);

  const handleBellClick = () => {
    const opening = !open;
    setOpen(opening);
    if (opening) {
      fetchNotifications();
      markAllRead();
    }
  };

  const respond = async (notif, action) => {
    try {
      if (notif.type === 'room_invite') {
        await axios.post(
          `${API}/rooms/${notif.id}/respond/`,
          { action },
          { headers: { Authorization: `Bearer ${token()}` } }
        );
      } else {
        await axios.post(
          `${API}/notifications/${notif.id}/respond/`,
          { action },
          { headers: { Authorization: `Bearer ${token()}` } }
        );
      }
      setNotifs(prev => prev.filter(n => n.id !== notif.id));
      if (notif.type === 'follow_request' && action === 'accept' && onFollowChanged) {
        onFollowChanged();
      }
      if (notif.type === 'room_invite' && action === 'accept') {
        // Recargar la página para que aparezca la nueva sala en el sidebar
        window.location.reload();
      }
    } catch {}
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="nb-wrap" ref={panelRef}>
      <button className="nb-bell-btn" onClick={handleBellClick} title={t('notifications')}>
        <span className="nb-bell-icon">🔔</span>
        {count > 0 && (
          <span className="nb-badge">{count > 99 ? '99+' : count}</span>
        )}
      </button>

      {open && (
        <div className="nb-panel">
          <div className="nb-panel-header">
            <span>{t('notifications')}</span>
            {notifications.length > 0 && (
              <span className="nb-panel-count">{notifications.length}</span>
            )}
          </div>

          {loading && <div className="nb-loading">{t('loading_dots')}</div>}

          {!loading && notifications.length === 0 && (
            <div className="nb-empty">{t('no_notifications')}</div>
          )}

          {notifications.map(notif => (
            <div
              key={`${notif.type}-${notif.id}`}
              className={`nb-item${notif.is_read ? ' nb-item--read' : ''}`}
            >
              {!notif.is_read && <span className="nb-unread-dot" />}
              <Avatar username={notif.from_username} photoUrl={notif.from_photo_url} />
              <div className="nb-item-body">
                {notif.type === 'room_invite' ? (
                  <span className="nb-item-text">
                    <strong>@{notif.from_username}</strong> {t('invited_to_room')} <strong>🔒 {notif.room_name}</strong>
                  </span>
                ) : notif.type === 'mention' ? (
                  <span className="nb-item-text">
                    <strong>@{notif.from_username}</strong> {t('mentioned_you')} <em className="nb-mention-preview">"{notif.post_text}"</em>
                  </span>
                ) : notif.type === 'shared_commercialization' ? (
                  <span className="nb-item-text">
                    <strong>@{notif.from_username}</strong> {t('shared_commercialization')}
                  </span>
                ) : notif.type === 'repost' ? (
                  <span className="nb-item-text">
                    <strong>@{notif.from_username}</strong>{' '}
                    {notif.is_quote ? t('quoted_your_q') : t('reposted_your_q')}
                    {notif.post_text && <em className="nb-mention-preview"> "{notif.post_text}"</em>}
                  </span>
                ) : notif.type === 'room_post' ? (
                  <span className="nb-item-text">
                    <strong>@{notif.from_username}</strong> {t('posted_in_room')} <strong>🔒 {notif.room_name}</strong>
                    {notif.has_audio
                      ? <em className="nb-mention-preview"> 🎤 {t('voice_note')}</em>
                      : notif.post_text && <em className="nb-mention-preview"> "{notif.post_text}"</em>
                    }
                  </span>
                ) : notif.status === 'pending' ? (
                  <span className="nb-item-text">
                    <strong>@{notif.from_username}</strong> {t('wants_to_follow')}
                  </span>
                ) : (
                  <span className="nb-item-text nb-item-text--follow">
                    <strong>@{notif.from_username}</strong> {t('started_following')}
                  </span>
                )}
                <span className="nb-item-time">{timeAgo(notif.created_at)}</span>
              </div>

              {notif.status === 'pending' && (
                <div className="nb-item-actions">
                  <button className="nb-btn nb-btn--accept" onClick={() => respond(notif, 'accept')}>
                    {t('accept')}
                  </button>
                  <button className="nb-btn nb-btn--reject" onClick={() => respond(notif, 'reject')}>
                    {t('reject')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
